import { Skeleton } from "@chakra-ui/react";
import React, { useMemo } from "react";
import { GenericError } from "src/components/Feedback/GenericError";
import { SearchSelect } from "src/components/Inputs/SearchSelect";
import {
  Item,
  SearchSelectProps,
} from "src/components/Inputs/SearchSelect/SearchSelect";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { useAdminOrganizations } from "src/hooks/useOrganizations";
import * as RD from "src/types/remoteData";

type OrganizationItem = Item & {
  organization: {
    id: uuid;
    name: string;
    path: string;
  };
};

type Props = {
  organizationId: string | null;
  label?: React.ReactNode;
  onChange: (id: string | null) => void;
} & Omit<
  SearchSelectProps<OrganizationItem>,
  "selectedItem" | "label" | "items" | "onChange"
>;

export const OrganizationSelect: React.FC<Props> = ({
  organizationId,
  onChange,
  label = "Organization",
  placeholder = "Search organization",
  disabled = false,
  error,
  ...props
}) => {
  const organizationsData = useAdminOrganizations();

  const organizationsRD = useMemo(() => {
    return organizationsData.map((organizations) => {
      return organizations.map((organization) => ({
        key: organization.id,
        label: organization.name,
        description: organization.path,
        organization,
      }));
    });
  }, [organizationsData]);

  const selectedOrganizationRD = useMemo(() => {
    return organizationsRD.map((organizations) => {
      return (
        organizations.find(
          (organization) => organization.key === organizationId
        ) ?? null
      );
    });
  }, [organizationId, organizationsRD]);

  return (
    <RemoteDataView
      remoteData={RD.toTuple(organizationsRD, selectedOrganizationRD)}
      error={(apolloError) => <GenericError error={apolloError as Error} />}
      loading={<Skeleton width="100%" height={10} marginTop={2} />}
    >
      {([organizations, selectedOrganization]) => (
        <SearchSelect<OrganizationItem>
          variant="unlocked"
          label={label}
          items={organizations}
          selectedItem={selectedOrganization}
          onChange={(item) => onChange(item?.organization.id ?? null)}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
          renderEmptyItems={() => "No organizations found"}
          {...props}
        />
      )}
    </RemoteDataView>
  );
};
